import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'

import { sortBySpecificArray } from '../../../utils'

import Loader from '../../atoms/Loader'
import Header from './Header'
import Body from './Body'
import GroupHeaderContent from './GroupHeaderContent'

// DON NOT REMOVE the comments around 'import style.scss'
// ###STYLE_REPLACEMENT_START###
import './style.scss'
// ###STYLE_REPLACEMENT_END###

const Table = ({
  id,
  title,
  columns,
  dataSource,
  size,
  isScrollable,
  isAutoFitWidth,
  withoutDataPlaceholder,
  customGroupHeaderContent,
  customGroupOrder,
  groupByOptions,
  showCollapseAll,
  defaultCollapseAllStatus,
  extraClasses,
  disclaimer,
  callback,
  callbackExpandAll
}) => {
  const collapseIconSize = 16
  const [headers, setHeaders] = useState()
  const [bodyDataSource, setBodyDataSource] = useState()
  const [collapsable, setCollapsable] = useState()
  const [isExpandedAll, setExpandedAllStatus] = useState(defaultCollapseAllStatus)
  const [autoHeaderCellWidth, setAutoHeaderCellWidth] = useState()
  const [headerWithChildren, setHeaderWithChildren] = useState()
  const [isFitContent, setFitContentStatus] = useState(true)
  const isFitContentRef = useRef(isFitContent)
  const setFitContentStatusRef = (x) => {
    isFitContentRef.current = x
    setFitContentStatus(x)
  }

  const [savedFlatHeaders, saveFlatHeaders] = useState()
  const savedFlatHeadersRef = useRef(savedFlatHeaders)
  const saveFlatHeadersRef = (x) => {
    savedFlatHeadersRef.current = x
    saveFlatHeaders(x)
  }

  const [savedHeaderStyles, saveHeaderStyles] = useState()
  const savedHeaderStylesRef = useRef(savedHeaderStyles)
  const saveHeaderStylesRef = (x) => {
    savedHeaderStylesRef.current = x
    saveHeaderStyles(x)
  }

  const [sortConfig, setSortConfig] = useState()
  const savedSortConfigRef = useRef(sortConfig)
  const setSortConfigRef = (x) => {
    savedSortConfigRef.current = x
    setSortConfig(x)
  }

  const [savedDimension, saveDimension] = useState()
  const savedDimensionRef = useRef(savedDimension)
  const saveDimensionRef = (x) => {
    savedDimensionRef.current = x
    saveDimension(x)
  }

  const targetRef = useRef()

  const getSortItemValue = (data, sortDataIndex, type) => {
    const sortableValue = _.get(data.rowData, sortDataIndex)
    return type.toLowerCase() === 'string'
      ? sortableValue.toLowerCase()
      : _.isNaN(parseFloat(sortableValue))
        ? 0
        : parseFloat(sortableValue)
  }

  const getDefaultSorters = ({ sortType, sortOrder, sortDataIndex }) => {
    const sorters = {
      number: (a, b) => {
        const aValue = getSortItemValue(a, sortDataIndex, 'number')
        const bValue = getSortItemValue(b, sortDataIndex, 'number')
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue
      },
      string: (a, b) => {
        const aValue = getSortItemValue(a, sortDataIndex, 'string')
        const bValue = getSortItemValue(b, sortDataIndex, 'string')
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }
    }
    return sorters[sortType.toLowerCase()]
  }

  const commonSort = ({ bodyData = bodyDataSource, sortType, sortOrder, sortDataIndex, sorter, fromHandleSort = false, ...restInfo }) => {
    const hasParentNode = !!bodyData[0].parentNode
    let tempSorter = null
    let sortedBody = null

    if (sorter) {
      tempSorter = sorter
    } else if (sortDataIndex) {
      tempSorter = getDefaultSorters({ sortType, sortOrder, sortDataIndex })
    }

    const tempData = _.clone(bodyData)

    if (hasParentNode) {
      sortedBody = bodyData.map((dataItem) => {
        const { parentNode, children } = dataItem
        const tempChildren = _.clone(children)
        const sortedChildren = tempChildren.sort(tempSorter)
        return {
          expanded: isExpandedAll,
          parentNode,
          children: sortedChildren
        }
      })
    } else {
      sortedBody = tempData.sort(tempSorter)
    }

    callback({ sortOrder, key: restInfo.key, fromHandleSort })

    return sortedBody
  }

  const groupBody = (bodyData, groupByKey) => {
    let groupedBody = _(bodyData)
      .groupBy((dataItem) => _.get(dataItem.rowData, groupByKey))
      .toPairs()
      .map(([groupName, groupValue]) => ({
        parentNode: {
          cells: [
            {
              name: groupName,
              element: <GroupHeaderContent {...{ customGroupHeaderContent, groupName, groupValue }} />
            }
          ]
        },
        expanded: isExpandedAll,
        children: groupValue
      }))
      .sortBy((dataItem) => _.get(dataItem, 'parentNode.cells[0].name'))
      .value()

    if (!_.isEmpty(customGroupOrder)) {
      groupedBody = sortBySpecificArray(groupedBody, customGroupOrder, 'parentNode.cells[0].name')
    }

    return groupedBody
  }

  const changeFitContentStatus = () => {
    if (targetRef.current?.offsetWidth) {
      if (targetRef.current?.scrollWidth > targetRef.current?.offsetWidth) {
        if (!isFitContentRef.current) {
          setFitContentStatusRef(true)
        }
      } else if (isFitContentRef.current) {
        setFitContentStatusRef(false)
      }
    }
  }

  const calculateHeaderCellWidth = (flatHeaders, headerStyles) => {
    if (targetRef.current?.offsetWidth) {
      const filteredHeaderStyles = headerStyles.filter((style) => !!style.width)
      if (filteredHeaderStyles.length !== flatHeaders.length) {
        const tableWidth = targetRef.current.offsetWidth
        const { percentageWidth, pixelWidth } = _.groupBy(filteredHeaderStyles, (style) =>
          style.width.includes('%') ? 'percentageWidth' : 'pixelWidth'
        )

        let allocatableWidth = tableWidth
        if (percentageWidth) {
          const setCellsPercentageWidth = filteredHeaderStyles.reduce(
            (previousStyle, currentStyle) => previousStyle + parseFloat(currentStyle.width),
            0
          )

          if (setCellsPercentageWidth > 100) {
            const eachHeaderCellWidth = `${100 / flatHeaders.length}%`
            return eachHeaderCellWidth
          }

          allocatableWidth = ((100 - setCellsPercentageWidth) / 100) * tableWidth
        }

        if (pixelWidth) {
          const setCellsPixelWidth = filteredHeaderStyles.reduce(
            (previousStyle, currentStyle) => previousStyle + parseFloat(currentStyle.width),
            0
          )

          if (setCellsPixelWidth > allocatableWidth) {
            const eachHeaderCellWidth = `${100 / flatHeaders.length}%`
            return eachHeaderCellWidth
          }

          allocatableWidth -= setCellsPixelWidth
        }

        const eachHeaderCellWidth = `${(allocatableWidth / tableWidth / (flatHeaders.length - filteredHeaderStyles.length)) * 100}%`

        return eachHeaderCellWidth
      }
    }
    return ''
  }

  let sortableFlag = false
  let tempDefaultSortKey = ''
  let tempDefaultSortOrder = 'asc'

  const getSortInfo = (headerCell) => {
    const { sortable, isDefaultSort, sortOrder, key } = headerCell

    if (!sortableFlag) {
      sortableFlag = !!sortable
    }

    // set when has default sort cell
    if (isDefaultSort) {
      tempDefaultSortKey = key
      tempDefaultSortOrder = sortOrder || tempDefaultSortOrder
    }

    // set first sortable cell without default sort cell
    if (!!sortable && !tempDefaultSortKey) {
      tempDefaultSortKey = key
      tempDefaultSortOrder = sortOrder || tempDefaultSortOrder
    }
  }

  const parseHeaderRows = (rootColumns) => {
    const flatHeaders = []
    const headerStyles = []
    let hasChildren = false

    const fillRowCells = (allColumns) => {
      const formattedColumns = allColumns.map((column) => {
        const subColumns = column.children
        if (subColumns && subColumns.length > 0) {
          if (!hasChildren) {
            hasChildren = true
          }
          const newChildren = fillRowCells(subColumns)
          return {
            ...column,
            children: newChildren
          }
        }

        const newColumn = { ...column, ...(dataSource ? { isStatic: false } : { isStatic: true }) }

        getSortInfo(newColumn)
        flatHeaders.push(newColumn)
        headerStyles.push(newColumn.style || {})

        return newColumn
      })

      return formattedColumns
    }

    const headerRows = fillRowCells(rootColumns)

    if (isAutoFitWidth) {
      const eachHeaderCellWidth = calculateHeaderCellWidth(flatHeaders, headerStyles)
      setAutoHeaderCellWidth(eachHeaderCellWidth)
    }

    saveFlatHeadersRef(flatHeaders)
    saveHeaderStylesRef(headerStyles)
    setSortConfigRef({
      sortable: sortableFlag,
      defaultSortKey: tempDefaultSortKey,
      defaultSortOrder: tempDefaultSortOrder
    })

    if (hasChildren) {
      setHeaderWithChildren(hasChildren)
    }

    return { formattedHeaders: headerRows, flatHeaders }
  }

  const parseBodyRows = ({ flatHeaders, headerDimensions }) =>
    dataSource.map((dataItem) => {
      const renderedCells = flatHeaders.map((flatCell, flatCellIndex) => {
        const { dataIndex, cell, style = {} } = flatCell
        const { render, ...restCellProps } = cell
        const dataFeed = dataIndex ? dataItem[dataIndex] : dataItem
        const { isHighPriority, ...restStyles } = headerDimensions[flatCellIndex]
        let cellStyles = isHighPriority ? { ...style, ...restStyles } : { ...restStyles, ...style }
        if (collapsable && !flatCellIndex && cellStyles?.width) {
          cellStyles = { ...cellStyles, width: `${parseFloat(cellStyles.width) + collapseIconSize}px` }
        }
        return {
          element: render(dataFeed),
          style: cellStyles,
          ...restCellProps
        }
      })

      return {
        rowData: dataItem,
        cells: renderedCells
      }
    })

  const groupBodyData = ({ option, data }) => {
    if (typeof option === 'string') {
      return groupBody(data, option)
    }

    return option(data)
  }

  const generateBody = (headerDimensions) => {
    const { sortable, defaultSortKey, defaultSortOrder } = savedSortConfigRef.current
    let body = dataSource

    if (dataSource && !_.isEmpty(dataSource)) {
      body = parseBodyRows({ flatHeaders: savedFlatHeadersRef.current, headerDimensions })

      if (groupByOptions) {
        body = groupBodyData({ data: body, option: groupByOptions })
      }

      if (sortable) {
        const defaultSortCell = _.find(savedFlatHeadersRef.current, (headerItem) => headerItem.key === defaultSortKey)
        body = commonSort({ bodyData: body, ...defaultSortCell, sortType: defaultSortCell.type, sortOrder: defaultSortOrder })
      }
    }

    if (body) {
      saveDimensionRef(headerDimensions)
      setBodyDataSource(body)
    }
  }

  const onHandleSort = (cell) => {
    const sortedBodyData = commonSort({ ...cell, sortType: cell.type, fromHandleSort: true })
    setBodyDataSource(sortedBodyData)
  }

  const handleExpandStatus = ({ isControlAll, expandedAllStatus, expanded, rowIndex }) => {
    if (isControlAll) {
      const modifiedBodyData = bodyDataSource.map((bodyItem) => {
        return {
          ...bodyItem,
          expanded: expandedAllStatus
        }
      })
      setBodyDataSource(modifiedBodyData)
      setExpandedAllStatus(expandedAllStatus)
      callbackExpandAll(expandedAllStatus)
    } else {
      _.set(bodyDataSource[rowIndex], 'expanded', expanded)
      const newExpandedStatus = !!_.find(bodyDataSource, (dataItem) => dataItem.expanded)

      if (newExpandedStatus !== isExpandedAll) {
        setExpandedAllStatus(newExpandedStatus)
      }
    }
  }

  const syncDimensions = (dimension) => {
    generateBody(dimension)
  }

  const handleResize = () => {
    if (isAutoFitWidth) {
      changeFitContentStatus()
      const eachHeaderCellWidth = calculateHeaderCellWidth(savedFlatHeadersRef.current, savedHeaderStylesRef.current)
      setAutoHeaderCellWidth(eachHeaderCellWidth)

      const newHeaderDimensions = savedHeaderStylesRef.current.map((style) =>
        style.width
          ? style
          : {
              width: eachHeaderCellWidth
            }
      )

      generateBody(newHeaderDimensions)
    }
  }

  useEffect(() => {
    const { formattedHeaders } = parseHeaderRows(columns)
    setHeaders(formattedHeaders)
  }, [columns, dataSource])

  useEffect(() => {
    if (savedDimensionRef.current) {
      generateBody(savedDimensionRef.current)
    }
    setCollapsable(showCollapseAll || (!!groupByOptions && typeof groupByOptions !== 'function'))
  }, [groupByOptions])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    if (isAutoFitWidth) {
      setTimeout(() => {
        changeFitContentStatus()
      }, 0)
    }
  }, [])

  return (
    <React.Fragment>
      {title && <div className="fil-table--title text-heading-s">{title}</div>}
      <div
        {...(id ? { id } : {})}
        ref={targetRef}
        className={classnames('gdpp-components fil-table-wrapper', {
          scrollable: isScrollable,
          [extraClasses]: extraClasses
        })}
      >
        <div className={classnames('fil-table', { 'fit-content': isFitContentRef.current, 'fil-table--small': size === 'small' })}>
          {headers && (
            <Header
              extraClasses={classnames({ 'fixed-header': isAutoFitWidth })}
              syncDimensions={syncDimensions}
              {...(isAutoFitWidth ? { autoHeaderCellWidth } : {})}
              header={headers}
              headerWithChildren={headerWithChildren}
              sortable={savedSortConfigRef.current.sortable && !_.isEmpty(dataSource)}
              collapsable={collapsable}
              expandedAll={isExpandedAll}
              onHandleSort={onHandleSort}
              onHandleExpandAll={(expandedAllStatus) => {
                handleExpandStatus({ isControlAll: true, expandedAllStatus })
              }}
            />
          )}
          {bodyDataSource ? (
            <Body
              dataSource={bodyDataSource}
              withoutDataPlaceholder={withoutDataPlaceholder}
              onHandleExpand={(row) => {
                handleExpandStatus(row)
              }}
            />
          ) : (
            <div className="fil-table--loader-container">
              <Loader />
            </div>
          )}
        </div>
      </div>
      {disclaimer && <div className="fil-table--disclaimer">{disclaimer}</div>}
    </React.Fragment>
  )
}

Table.displayName = 'Table'

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  dataSource: PropTypes.array,
  id: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  isScrollable: PropTypes.bool,
  withoutDataPlaceholder: PropTypes.string,
  customGroupOrder: PropTypes.array,
  customGroupHeaderContent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  groupByOptions: (props, propName, componentName) => {
    const validOptions = ['string', 'function']
    const option = props[propName]
    if (!validOptions.includes(typeof option)) {
      return new Error(
        `Validation failed. Valid prop ${propName} supplied to ${componentName} should be one of [''(Empty string, it's the default prop value), a property in data source object] or function for custom group logic. `
      )
    }

    return null
  },
  extraClasses: PropTypes.string,
  showCollapseAll: PropTypes.bool,
  defaultCollapseAllStatus: PropTypes.bool,
  isAutoFitWidth: PropTypes.bool,
  size: PropTypes.string,
  disclaimer: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  callback: PropTypes.func,
  callbackExpandAll: PropTypes.func
}

Table.defaultProps = {
  dataSource: null,
  id: '',
  title: '',
  isScrollable: true,
  withoutDataPlaceholder: 'No Data',
  customGroupOrder: [],
  customGroupHeaderContent: '',
  groupByOptions: '',
  extraClasses: '',
  showCollapseAll: false,
  defaultCollapseAllStatus: false,
  isAutoFitWidth: false,
  size: '',
  disclaimer: '',
  callback: () => {},
  callbackExpandAll: () => {}
}

export default Table
